<template>
  <div
    id="ui-chess"
    class="ui-chess"
  >
    <ui-loading
      :is-open="loading"
      class="ui-chess__loading"
    />
    <div class="ui-chess-header">
      <template v-if="!initLoading">
        <div class="ui-chess-counts">
          <p>Всего объектов:<span v-if="filterRoomsCount"> {{ filterRoomsCount }} из </span> {{ allRoomsCount }}</p>
        </div>
        <div class="ui-chess-options">
          <ui-switch
              v-if="hasSwitchRoomCount"
              v-model="hasRoomCount"
              title="Комнаты"/>
          <ui-switch
              v-if="hasSwitchStatus"
              v-model="hasStatus"
              title="Статусы"
          />
          <div class="ui-chess-switcher">
            <span>Размер</span>
            <ui-switcher v-model="activeSize">
              <ui-switcher-item
                  v-for="size in sizes"
                  :key="size"
                  :title="size"
                  :id="size"
                  :active-id="activeSize"
              />
            </ui-switcher>
          </div>
        </div>
      </template>
      <template v-else>
        <ui-skeleton
            :width="120"
            :border-radius="4"
            text
        />
        <ui-skeleton
            :width="200"
            :height="34"
            :border-radius="4"
        />
      </template>
    </div>
    <div class="ui-chess-body-wrap">
      <div
        class="ui-chess-body"
        id="ui-chess-body"
        :class="{'one-column': isOneColumn}"
        ref="sections">
        <p v-if="!isOneColumn" class="ui-chess-body-subtitle__floor">ЭТАЖИ</p>
        <template v-if="!initLoading">
          <ui-chess-section
            v-for="section in house.sections"
            :key="section.id"
            :section="section"
            :has-color-status="hasStatus"
            :size="activeSize"
            :selected-rooms="innerSelectedRoomIds"
            :has-room-count="hasRoomCount"
            :has-room-comment="hasRoomComment"
            :filter-rooms="filterRooms"
            :has-grades="hasGrades"
            @set-active-room="setActiveRoom"
            @open-action-modal="openActionModal($event, true)"
            @close-action-modal="closeActionModal"
            @click-room="eventRoomClick"
          />
          <ui-chess-section
            v-if="withoutSection"
            :section="withoutSection"
            :size="activeSize"
            :selected-rooms="innerSelectedRoomIds"
            :has-color-status="hasStatus"
            :has-room-count="hasRoomCount"
            :has-room-comment="hasRoomComment"
            :filter-rooms="filterRooms"
            :has-grades="hasGrades"
            @set-active-room="setActiveRoom"
            @open-action-modal="openActionModal($event, true)"
            @close-action-modal="closeActionModal"
            @click-room="eventRoomClick"
          />
        </template>
        <div
          v-else
          class="ui-chess-skeletons"
        >
          <div
            v-for="floor in 9"
            :key="`skeleton-floor-${floor}`"
            class="ui-chess-skeletons__floor"
          >
            <ui-skeleton
              v-for="room in getRandomNumber(10, 15)"
              :key="`skeleton-room-${room}`"
              :height="40"
              :width="40"
              :border-radius="4"
            />
          </div>
        </div>
      </div>
      <ui-chess-menu
        :is-open="roomSelection.isOpenActionModal"
        :event="roomSelection.eventSecondClick"
        :list-actions="actions"
        :select-count="selectCount"
        :is-hover-menu="isHoverMenu"
        :room="activeRoom"
        @close="closeActionModal(true)"
      />
    </div>
    <ui-chess-legends
      class="ui-chess__legends"
      v-if="isShowLegends"
      :legends="legends"
      :has-grades="hasGrades"
      :loading="loading"
    />
  </div>
</template>

<script>
import UiChessSection from './UiChessSection.vue'
import UiSwitch from '../switch/UiSwitch.vue'
import UiSwitcher from '../switcher/UiSwitcher.vue'
import UiSwitcherItem from '../switcher/UiSwitcherItem.vue'
import { COMPONENT_SIZE } from '../../libs/helpConsts'
import { RoomSelection } from './chessHelp'
import UiChessMenu from './UiChessMenu.vue'
import UiSkeleton from '../skeleton/UiSkeleton.vue'
import { getRandomNumber } from '../../libs/helpTools'
import UiLoading from '../loader/UiLoading.vue'
import UiChessLegends from './UiChessLegends.vue'

export default {
  name: 'UiChess',
  components: {
    UiLoading,
    UiSkeleton,
    UiChessMenu,
    UiSwitcherItem,
    UiSwitcher,
    UiSwitch,
    UiChessSection,
    UiChessLegends
  },
  props: {
    house: {
      type: Object,
      default: () => ({})
    },

    legends: Array,

    actions: {
      type: Array,
      default: () => ([])
    },

    filterRooms: {
      type: Array
    },

    selectedRoomIds: {
      type: Array
    },

    customSizes: {
      type: Array,
      default: () => COMPONENT_SIZE
    },

    initActiveSize: {
      type: String,
      default: 'm'
    },

    initStatusValue: {
      type: Boolean,
      default: false
    },

    hasSwitchStatus: {
      type: Boolean,
      default: true
    },

    hasSwitchRoomCount: {
      type: Boolean,
      default: true
    },

    hasMultiSelect: {
      type: Boolean,
      default: false
    },

    isOneColumn: {
      type: Boolean,
      default: false
    },

    hasRoomComment: {
      type: Boolean,
      default: false
    },

    hasGrades: Boolean,

    loading: {
      type: Boolean,
      default: false
    },

    initLoading: {
      type: Boolean,
      default: false
    }
  },

  data () {
    return {
      hasStatus: this.initStatusValue,
      hasRoomCount: false,
      activeSize: this.initActiveSize,
      roomSelection: {},
      chessSelectedRoomIds: [],
      activeRoom: {},
      isHoverMenu: false
    }
  },

  mounted () {
    this.init()
  },

  computed: {
    innerSelectedRoomIds: {
      set (value) {
        this.setSelectedRoomIds(value)
      },

      get () {
        return this.selectedRoomIds ? this.selectedRoomIds : this.chessSelectedRoomIds
      }
    },

    allRoomsCount () {
      const withoutSectionCount = this.house.floors?.reduce((accum, floor) => {
        accum += floor.rooms?.length || 0
        return accum
      }, 0) || 0
      const initCount = this.house.rooms?.length + withoutSectionCount || 0
      return this.house.sections?.reduce((accum, section) => {
        accum += (section.rooms?.length || 0)
        accum += section.floors.reduce((accum, floor) => {
          accum += floor.rooms?.length || 0
          return accum
        }, 0)
        return accum
      }, initCount)
    },

    filterRoomsCount () {
      if (!this.filterRooms) return
      return this.filterRooms.length
    },

    withoutSection () {
      if (!this.house.floors?.length && !this.house?.rooms?.length) return
      return {
        name: 'Без секции',
        id: 'without-section',
        rooms: this.house.rooms,
        floors: this.house.floors
      }
    },

    sizes () {
      return this.customSizes
    },

    selectCount () {
      return this.innerSelectedRoomIds.length
    },

    isChoiceSomeRooms () {
      return this.selectCount > 1
    },

    isShowLegends () {
      return Boolean(this.legends?.length || this.initLoading)
    }
  },

  methods: {
    getRandomNumber,

    init () {
      this.initRoomSelect()
    },

    setActiveRoom (room) {
      if (this.isChoiceSomeRooms) {
        this.activeRoom = {}
      } else {
        this.activeRoom = room
      }
    },

    initRoomSelect () {
      this.roomSelection = new RoomSelection(this.$refs.sections, this.innerSelectedRoomIds, this.hasMultiSelect, this.eventRoomClick)
    },

    closeActionModal (isForce) {
      if (!this.isHoverMenu && !isForce) return
      this.roomSelection.openActionModal(false)
      this.activeRoom = {}
    },

    openActionModal ({ event, room }, isHover = false) {
      if (this.selectCount) return
      if (isHover && this.roomSelection.getIsBegin()) return
      this.isHoverMenu = isHover
      this.roomSelection.openActionModal(true, event)
      this.setActiveRoom(room)
    },

    setSelectedRoomIds (value) {
      this.chessSelectedRoomIds = value
      this.isHoverMenu = false
      this.$emit('update:selected-room-ids', value)
    },

    eventRoomClick (roomId) {
      this.$emit('room-click', roomId)
    }
  },

  watch: {
    'roomSelection.selectedRoomIds' (value) {
      this.setSelectedRoomIds(value)
    },

    hasMultiSelect (value) {
      if (value) {
        this.initRoomSelect()
      } else {
        this.roomSelection?.destroy?.()
        this.innerSelectedRoomIds = []
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.ui-chess {
  @include hidden-scrollbar;
}

.ui-chess-header {
  @include flex-wrap-list(0px, 16px, 8px, 8px, 8px, 8px);
  @include font-16;

  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 24px;
  color: $gray-3;
}

.ui-chess-options {
  @include flex-wrap-list(16px);
  display: flex;
  align-items: center;

  @include children {
    &:last-child {
      margin-right: 0;
    }
  }
}

.ui-chess-counts {
  @include flex-wrap-list(16px);

  p {
    margin-bottom: 0;
    color: $gray-3;
  }
}

.ui-chess-body-wrap {
  position: relative;
  z-index: 1;
}

.ui-chess-body {
  position: relative;
  display: flex;
  align-items: flex-start;
  overflow-x: scroll;
  padding-bottom: 20px;
  user-select: none;

  &.one-column {
    flex-direction: column;
  }

  @include children {
    margin-right: 50px;
  }

  @include pretty-scrollbar-m;
}

.ui-chess-switcher {
  display: flex;
  align-items: center;

  span {
    margin-right: 8px;
  }

  .ui-switcher-item {
    @include font-14;

    text-transform: uppercase;
    height: 34px;
  }
}

.ui-chess-body-subtitle__floor {
  @include flex-center;
  @include font-14;
  margin: auto 30px auto 0;
  font-weight: bold;
  writing-mode: tb-rl;
  transform: rotate(180deg);

  letter-spacing: 2px;
}

.ui-chess-skeletons {
  display: flex;
  flex-direction: column;
  align-items: center;
  flex: 1;

  &__floor {
    @include flex-wrap-list(8px, 8px, 4px, 4px, 4px 4px)
  }
}

.ui-chess__loading {
  position: absolute;
  z-index: 10;
}

.ui-chess__legends {
  padding-top: 24px;
}

@include media-max-wide-sm {
  .ui-chess-counts {
    margin-bottom: 8px;
  }
}
</style>
<style lang="scss">
.select-rectangle {
  position: fixed;
  z-index: 99999;
  border: 2px dashed $gray-1;
}
</style>
